import { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";

import usePageTitle from "Hooks/usePageTitle";
import useUserContext from "Hooks/useUserContext";
import EditModal from "Pages/Setup/TestConnection/Components/EditModal/EditModal";
import { useCompanyConfig } from "Pages/Setup/Connect/connectShared";
import {
    CMIC_FIELDS,
    CMIC_TESTS,
    getRampFields,
    getRampOauthRefreshTokenSuffix,
    INTEGRATIONS,
    JDE_INTEGRATIONS,
    JDE_TESTS,
    MRI_INTEGRATIONS,
    MRI_TESTS,
    POSTING_FIELDS,
    REIMBURSEMENT_VENDOR_FIELDS,
    RAMP_TESTS,
    SPECTRUM_INTEGRATIONS,
    SPECTRUM_TESTS,
    SecondarySystemKey,
} from "Pages/Setup/Connect/connectData";
import RampConnect from "Pages/Setup/Connect/views/RampConnect";
import CmicConnect from "Pages/Setup/Connect/views/CmicConnect";
import MriConnect from "./views/MriConnect";
import SpectrumConnect from "./views/SpectrumConnect";
import JdeConnect from "./views/JdeConnect";
import SystemTests from "Pages/Setup/Connect/views/SystemTests";
import MapData from "Pages/Setup/Connect/views/MapData";
import MriMapData from "./views/MriMapData";
import SpectrumMapData from "./views/SpectrumMapData";
import JdeMapData from "./views/JdeMapData";
import IntegrationDetail from "Pages/Setup/Connect/views/IntegrationDetail";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

type View = "ramp" | "ramptest" | "secondary" | "secondarytest" | "map" | "int";

export default function Onboarding() {
    usePageTitle("Connect");
    const [userContext] = useUserContext();
    const { loaded, companyId, find, onEdit, onSave, onRefresh, modalProps } =
        useCompanyConfigAdapter();

    const systems = (userContext.companySystems ?? "").toLowerCase();
    const hasCmic = systems.includes("cmic");
    const hasMri = systems.includes("mrix") || systems.includes("mri");
    const hasSpectrum = systems.includes("spectrum");
    const hasJde = systems.includes("jde");
    const secondarySystem: SecondarySystemKey = hasCmic
        ? "cmic"
        : hasMri
        ? "mrix"
        : hasSpectrum
        ? "spectrum"
        : "jde";
    const secondarySystemName =
        secondarySystem === "cmic"
            ? "CMiC"
            : secondarySystem === "mrix"
            ? "MRI"
            : secondarySystem === "spectrum"
            ? "Spectrum"
            : "JDE";
    const supportsConnect = hasCmic || hasMri || hasSpectrum || hasJde;
    const rampFields = useMemo(() => getRampFields(secondarySystem), [secondarySystem]);
    const secondaryTests =
        secondarySystem === "cmic"
            ? CMIC_TESTS
            : secondarySystem === "mrix"
            ? MRI_TESTS
            : secondarySystem === "spectrum"
            ? SPECTRUM_TESTS
            : JDE_TESTS;
    const activeIntegrations =
        secondarySystem === "cmic"
            ? INTEGRATIONS
            : secondarySystem === "mrix"
            ? MRI_INTEGRATIONS
            : secondarySystem === "spectrum"
            ? SPECTRUM_INTEGRATIONS
            : JDE_INTEGRATIONS;

    const [view, setView] = useState<View>("ramp");
    const [intId, setIntId] = useState<string>(activeIntegrations[0].id);
    const [mapStep, setMapStep] = useState(1);

    useEffect(() => {
        if (!activeIntegrations.some((integration) => integration.id === intId)) {
            setIntId(activeIntegrations[0].id);
        }
    }, [activeIntegrations, intId]);

    useEffect(() => {
        if (secondarySystem !== "cmic" && mapStep !== 1) {
            setMapStep(1);
        }
    }, [mapStep, secondarySystem]);

    const goTo = (v: string) => {
        if (v.startsWith("int:")) {
            setIntId(v.slice(4));
            setView("int");
        } else {
            setView(v as View);
        }
    };

    // ----- connection state -----
    const rampReqMissing = rampFields.filter((f) => f.required).filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const rampConnected = !!find(getRampOauthRefreshTokenSuffix(secondarySystem))?.keyValue;
    const rampCredsReady = rampReqMissing === 0;
    const secondaryReqMissing = CMIC_FIELDS.filter((f) => f.required).filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const secondaryConnected = secondarySystem === "cmic" && secondaryReqMissing === 0;
    const connectedCount = (rampConnected ? 1 : 0) + (secondaryConnected ? 1 : 0);
    const hasValue = (suffix: string) => !!find(suffix)?.keyValue?.trim();
    const asBool = (suffix: string) => {
        const value = find(suffix)?.keyValue;
        if (!value) return false;
        return ["true", "1", "yes", "y", "on"].includes(
            value.trim().toLowerCase()
        );
    };

    // only require equipment costing journal on postingCompleteCount if integration.use_equipment_costing is true
    const useEquipmentCosting = asBool("integration.use_equipment_costing");
    const intercompanyState: "ok" | "todo" = find(
        "integration.use_custom_company_field"
    )
        ? "ok"
        : "todo";
    const postingCompleteCount = POSTING_FIELDS.filter((f) =>
        hasValue(f.suffix) || (f.suffix === "integration.cmic.equipment_journal" && !useEquipmentCosting)
    ).length;
    // the config just needs to exist for this to be complete
    const equipmentCostingState: "ok" | "todo" = find(
        "integration.use_equipment_costing"
    )
        ? "ok"
        : "todo";
    const keyPlayersComplete = hasValue("integration.cmic.job_approver_codes");
    const reimbursementVendorComplete = REIMBURSEMENT_VENDOR_FIELDS.every((f) => hasValue(f.suffix));

    const postingState: "ok" | "prog" | "todo" =
        postingCompleteCount === POSTING_FIELDS.length
            ? "ok"
            : postingCompleteCount > 0
            ? "prog"
            : "todo";
    const keyPlayersState: "ok" | "prog" | "todo" = keyPlayersComplete
        ? "ok"
        : "todo";
    const reimbursementVendorsState: "ok" | "prog" | "todo" =
        reimbursementVendorComplete ? "ok" : "todo";
    const receiptSyncingState: "ok" | "prog" | "todo" = asBool(
        "integration.ramp.allow_attachment_sync"
    )
        ? "ok"
        : "todo";

    const sysIcon = (state: "ok" | "prog" | "todo") =>
        state === "ok" ? "✓" : state === "prog" ? "!" : "°";
    const rampState: "ok" | "prog" | "todo" = rampConnected
        ? "ok"
        : rampCredsReady
        ? "prog"
        : "todo";
    const cmicState: "ok" | "prog" | "todo" = secondaryConnected
        ? "ok"
        : secondaryReqMissing < CMIC_FIELDS.filter((f) => f.required).length
        ? "prog"
        : "todo";
    const secondaryState: "ok" | "prog" | "todo" =
        secondarySystem === "cmic" ? cmicState : "todo";
    const icoClass = (s: string) =>
        s === "ok" ? styles.icoOk : s === "prog" ? styles.icoProg : styles.icoTodo;

    const integration =
        activeIntegrations.find((i) => i.id === intId) ?? activeIntegrations[0];

    const mapItems =
        secondarySystem === "cmic"
            ? [
                  {
                      label: "Allow Intercompany Expenses",
                      state: intercompanyState,
                  },
                  { label: "Enable Equipment Costing", state: equipmentCostingState },
                  { label: "Posting Defaults", state: postingState },
                  { label: "Key Players", state: keyPlayersState },
                  {
                      label: "Reimbursement Vendors",
                      state: reimbursementVendorsState,
                  },
                  {
                      label: "Receipt/Invoice Syncing",
                      state: receiptSyncingState,
                  },
              ]
            : [
                  {
                      label:
                          secondarySystem === "mrix"
                              ? "MRI mapping (coming soon)"
                              : "Spectrum mapping (coming soon)",
                      state: "todo" as const,
                  },
              ];

    if (!supportsConnect) {
        return (
            <Container fluid className={styles.page}>
                <h1 className={styles.title}>Connect your systems</h1>
                <p className={styles.sub}>
                    This company is not currently configured for a Ramp onboarding flow.
                </p>
            </Container>
        );
    }

    return (
        <Container fluid className={styles.page}>
            <h1 className={styles.title}>Connect your systems</h1>
            <p className={styles.sub}>
                Connect Ramp and {secondarySystemName}, map your data, and configure integration behavior.
            </p>

            <div className={styles.wrap}>
                {/* ---------- left rail ---------- */}
                <div>
                    {/* 1 · Connect systems */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>1 · Connect systems</div>
                        <div
                            className={`${styles.sys} ${
                                view === "ramp" || view === "ramptest"
                                    ? styles.sysActive
                                    : ""
                            }`}
                            onClick={() => goTo("ramp")}
                        >
                            <div className={`${styles.ico} ${icoClass(rampState)}`}>
                                {sysIcon(rampState)}
                            </div>
                            <div>
                                <div className={styles.nm}>Ramp</div>
                                <div className={styles.st}>
                                    {rampConnected
                                        ? "Connected"
                                        : rampCredsReady
                                        ? "Setup in progress"
                                        : "Not started"}
                                </div>
                            </div>
                        </div>
                        <div
                            className={`${styles.sys} ${
                                view === "secondary" || view === "secondarytest"
                                    ? styles.sysActive
                                    : ""
                            }`}
                            onClick={() => goTo("secondary")}
                        >
                            <div className={`${styles.ico} ${icoClass(secondaryState)}`}>
                                {sysIcon(secondaryState)}
                            </div>
                            <div>
                                <div className={styles.nm}>{secondarySystemName}</div>
                                <div className={styles.st}>
                                    {secondaryConnected
                                        ? "Connected"
                                        : secondaryState === "prog"
                                        ? "Setup in progress"
                                        : "Not started"}
                                </div>
                            </div>
                        </div>
                        <div className={styles.progWrap}>
                            <div className={styles.bar}>
                                <i style={{ width: `${(connectedCount / 2) * 100}%` }} />
                            </div>
                            <small>{connectedCount} of 2 systems connected</small>
                        </div>
                    </div>

                    {/* 2 · Map data */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>2 · Map data</div>
                        {mapItems.map((item, i) => (
                                <div
                                    key={item.label}
                                    className={`${styles.sys} ${
                                        view === "map" && mapStep === i + 1
                                            ? styles.sysActive
                                            : ""
                                    }`}
                                    onClick={() => {
                                        setMapStep(i + 1);
                                        goTo("map");
                                    }}
                                >
                                    <div
                                        className={`${styles.ico} ${icoClass(
                                            item.state
                                        )}`}
                                    >
                                        {sysIcon(item.state)}
                                    </div>
                                    <div className={styles.nm}>{item.label}</div>
                                </div>
                            ))}
                    </div>

                    {/* 3 · Integrations */}
                    <div className={styles.card}>
                        <div className={styles.cardHead}>3 · Integrations</div>
                        {activeIntegrations.map((it) => (
                            <div
                                key={it.id}
                                className={`${styles.sys} ${styles.cmp} ${
                                    view === "int" && intId === it.id
                                        ? styles.sysActive
                                        : ""
                                }`}
                                onClick={() => goTo(`int:${it.id}`)}
                            >
                                {it.id !== "default_behavior" && (
                                    <div className={`${styles.ico} ${icoClass(it.status)}`}>
                                        {sysIcon(it.status)}
                                    </div>
                                )}
                                <div className={styles.nm}>{it.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ---------- right panel ---------- */}
                <div>
                    {!loaded ? (
                        <div className={styles.panel}>
                            <div className={styles.sec}>Loading…</div>
                        </div>
                    ) : view === "ramp" ? (
                        <RampConnect
                            find={find}
                            onEdit={onEdit}
                            onRefresh={onRefresh}
                            companyId={companyId}
                            environment={userContext.environment}
                            secondarySystem={secondarySystem}
                            secondarySystemName={secondarySystemName}
                            goTo={goTo}
                        />
                    ) : view === "ramptest" ? (
                        <SystemTests
                            systemName="Ramp"
                            subtitle="Ramp Developer API"
                            logoText="R"
                            logoClass={styles.logoRamp}
                            groups={RAMP_TESTS}
                            connectionView="ramp"
                            goTo={goTo}
                            runSystem="Ramp"
                        />
                    ) : view === "secondary" ? (
                        secondarySystem === "cmic" ? (
                            <CmicConnect find={find} onEdit={onEdit} onSave={onSave} goTo={goTo} />
                        ) : secondarySystem === "mrix" ? (
                            <MriConnect goTo={goTo} />
                        ) : secondarySystem === "spectrum" ? (
                            <SpectrumConnect goTo={goTo} />
                        ) : (
                            <JdeConnect goTo={goTo} />
                        )
                    ) : view === "secondarytest" ? (
                        <SystemTests
                            systemName={secondarySystemName}
                            subtitle={
                                secondarySystem === "cmic"
                                    ? "14 read endpoints across 6 API programs"
                                    : secondarySystem === "mrix"
                                    ? "MRI endpoint tests are coming soon"
                                    : secondarySystem === "spectrum"
                                    ? "Spectrum endpoint tests are coming soon"
                                    : "JDE endpoint tests are coming soon"
                            }
                            logoText={
                                secondarySystem === "cmic"
                                    ? "CM"
                                    : secondarySystem === "mrix"
                                    ? "MR"
                                    : secondarySystem === "spectrum"
                                    ? "SP"
                                    : "JD"
                            }
                            logoClass={
                                secondarySystem === "cmic"
                                    ? undefined
                                    : secondarySystem === "mrix"
                                    ? styles.logoMri
                                    : secondarySystem === "spectrum"
                                    ? styles.logoSpectrum
                                    : styles.logoJde
                            }
                            groups={secondaryTests}
                            connectionView="secondary"
                            goTo={goTo}
                            runSystem={secondarySystem === "cmic" ? "CMiC" : undefined}
                        />
                    ) : view === "map" ? (
                        secondarySystem === "cmic" ? (
                            <MapData
                                find={find}
                                onEdit={onEdit}
                                onSave={onSave}
                                step={mapStep}
                                onStepChange={setMapStep}
                            />
                        ) : secondarySystem === "mrix" ? (
                            <MriMapData goTo={goTo} />
                        ) : secondarySystem === "spectrum" ? (
                            <SpectrumMapData goTo={goTo} />
                        ) : (
                            <JdeMapData goTo={goTo} />
                        )
                    ) : (
                        <IntegrationDetail
                            integration={integration}
                            find={find}
                            onEdit={onEdit}
                            onSave={onSave}
                            goTo={goTo}
                        />
                    )}
                </div>
            </div>

            <EditModal {...modalProps} />
        </Container>
    );
}

/** Adapts the shared hook's `findConfig`/`editField` to the shorter names the
 * view components expect. */
function useCompanyConfigAdapter() {
    const {
        loaded,
        companyId,
        findConfig,
        editField,
        saveField,
        refreshConfigs,
        modalProps,
    } =
        useCompanyConfig();
    return {
        loaded,
        companyId,
        find: findConfig,
        onEdit: editField,
        onSave: saveField,
        onRefresh: refreshConfigs,
        modalProps,
    };
}
